import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  DataGridPro,
  GridToolbar,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import moment from 'moment';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Button, Typography, Box, FormGroup, FormControlLabel, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import CustomPaper from "../Common/CustomPaper";
import CustomCellNumRender from "./CustomCellNumRender";
import {
  batteryIconGreen,
  batteryIconRed,
  batteryIconOrange,
  toggleIcon,
  DisConnectedLift, ConnectedLift
} from "../../assets";
import Switch from '@mui/material/Switch';
import { getLiftsList, updateLiftStatus, getLiftsSummary, updateLiftAlias, getLatestFirmwareVersion, updateLatestFirmwareVersion } from "../../redux/Thunk/RoutesThunk";
import { getRouteByIdData, getHeartBeatsByIdData } from "../../redux/Thunk/RoutesThunk";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { GlobalConstants } from "../../../utils/GlobalConstants";
import { Constants } from "../utils/Constants";
import { isSpareStatus, toLiftStatusLabel, liftStatusColor, nextLiftApiStatus } from "../utils/liftStatus";
import { formatFirmwareVersion } from "../utils/liftFirmware";
import { LIFT_FIELD_DEFINITIONS } from "../utils/liftFieldDefinitions";
import {
  assetTrackingIndicator,
  heartbeatIndicator,
  indicatorSortValue,
  INDICATOR_SORT_ORDER,
} from "../utils/liftIndicators";
import IndicatorCell from "./IndicatorCell";
import FirmwareVersionHeader from "./FirmwareVersionHeader";
import AdditionalInfoDialog from "../Common/AdditionalInfoDialog";
import InfoIcon from "@mui/icons-material/Info";
import PlaceIcon from "@mui/icons-material/Place";
import { hasPlottableCoords } from "../utils/liftLocation";
import { isMobile } from "../../../components/Common/helpers";

// Columns hidden on first load of the Lift Details grid. Still listed under
// COLUMNS, so they can be re-enabled; RESET returns to this default.
const DEFAULT_HIDDEN_LIFT_COLUMNS = {
  lastMaintenanceDate: false,
  maintenanceDueDate: false,
};

// Sorts the indicator columns worst-first, so sorting ascending surfaces the
// lifts that need attention. Plain alphabetical ordering of the state names
// would put CRITICAL after OK and bury the problems.
const indicatorSortComparator = (a, b) =>
  INDICATOR_SORT_ORDER.indexOf(a) - INDICATOR_SORT_ORDER.indexOf(b);

function LiftDataTable({ liftList, liftDataLoad, updateLiftStatus, updateStatus, updateAlias, updateAliasStatus, getLiftList, selectedCustomerId, selectedLocationId, getLiftSummary, selectedCustomerListData, selectedLocationListData, userRoleName, latestFirmwareVersion, latestFirmwareError, getLatestFirmware, updateLatestFirmware, onShowLiftOnMap }) {
  const canEditAlias = userRoleName === Constants.RPC_ADMIN
    || userRoleName === Constants.LOCATION_ADMIN
    || userRoleName === Constants.LOCATION_USER
    || userRoleName === Constants.CUSTOMER_ADMIN
    || userRoleName === Constants.CUSTOMER_USER;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sortLiftList, setSortLiftList] = useState();
  const [liftId, setLiftId] = useState("");
  const [liftStatus, setLiftStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [editingAliasId, setEditingAliasId] = useState(null);
  const [aliasInputValue, setAliasInputValue] = useState("");
  const [aliasConfirmOpen, setAliasConfirmOpen] = useState(false);
  const [pendingAlias, setPendingAlias] = useState({ liftId: null, liftName: null, value: "" });
  // "Additional Information" field definitions dialog, matching Route Status.
  const [additionalInfoOpen, setAdditionalInfoOpen] = useState(false);

  // TE-2011: latest released firmware version for the column header. One
  // fetch per mount; it is a single global value that changes rarely.
  useEffect(() => {
    if (getLatestFirmware) {
      getLatestFirmware();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 2,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#018015" : "#018015b3",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,.35)"
          : "rgba(0,0,0,.25)",
      boxSizing: "border-box",
    },
  }));

  const routeDetailsPage = (routeId, routeName) => {
    // routeId from the Lifts API is DUO_LIFT.ROUTE_NAME = LOCAL_ROUTE_ID (internal DB id).
    // Use getRoute (/v2/routes/{routeId}) directly — no locationId or deliveryDate needed.
    const routeData = [{
      routeId: routeId,
      routeName: routeName,
    }];

    localStorage.setItem("routeDBRoutes", JSON.stringify(routeData));
    GlobalConstants.IS_ROUTE_DETAILS_LOADING = false;
    GlobalConstants.SELETED_ROUTE_INDEX = 0;
    GlobalConstants.NAVIGATE_ROUTE_DETAILS = true;
    localStorage.setItem("routeIndex", 0);

    // Pre-fetch into Redux before navigating
    dispatch(getRouteByIdData(routeData));
    dispatch(getHeartBeatsByIdData(routeData));

    const event = new CustomEvent(Constants.ROUTE_SELECTION_EVENT, { detail: {} });
    document.dispatchEvent(event);
    navigate('/routedetails');
  }

  const liftMaintenancePage = (liftId) => {
    const event = new CustomEvent(Constants.LIFT_MAINTENANCE_EVENT, { detail: {} });
    document.dispatchEvent(event);
    navigate('/liftMaintenance', { state: { liftId: liftId } })
  }

  // TE-2087: open the Lift Map focused on this lift's pin with its popup
  // showing, instead of making the user zoom around hunting for it.
  //
  // This is a new, separate link. The Lift Name link (Alerts & Maintenance) and
  // the Location link (Route Details) are deliberately left as they were.
  //
  // The map is a view mode of this same /lifts page rather than a route, so
  // this hands off to LiftDashboard rather than calling navigate().
  const showLiftOnMap = (lift) => {
    if (!onShowLiftOnMap) return;
    onShowLiftOnMap({
      liftId: lift.liftId,
      latitude: lift.latitude,
      longitude: lift.longitude,
    });
  }

  const NumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ["contains" /* add more over time */].includes(value)
  );
  // const [pageSize, setPageSize] = useState(10);
  const [filterModel, setFilterModel] = useState({
    items: [
      {
        field: "rating",
        operator: ">",
        value: "2.5",
      },
    ],
  });

  const columns = [
    {
      field: "status", headerName: "Status", width: 120,
      // TE-2011: show "Spare" instead of "Out of Service". The valueGetter makes
      // quick search, the column filter and CSV export use the same label.
      valueGetter: (value) => toLiftStatusLabel(value),
      renderCell: (params) => {
        const isSpare = isSpareStatus(params.row.status);
        return (
          <>
            <Box sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: ' none'
              },
            }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <AntSwitch
                      defaultChecked={!isSpare}
                      onChange={() => handleToggle(params.row.liftId, params.row.status)}
                    />
                  }
                  label={<span style={{ color: liftStatusColor(params.row.status), fontSize: "13px" }}>{toLiftStatusLabel(params.row.status)}</span>}
                />
              </FormGroup>
            </Box>
          </>
        );
      }, filterOperators
    },
    {
      field: "liftName", headerName: "Lift Name", width: 175, renderCell: (params) => {
        // TE-2087: two destinations in one cell. The name keeps its existing
        // Alerts & Maintenance link; the pin beside it opens the Lift Map
        // focused on this lift. Both carry tooltips so the split is obvious.
        //
        // Kept in this cell rather than a column of its own so the grid gains
        // no width and the lift's identity sits next to its map link. Only the
        // renderCell changes, so sorting, filtering and CSV export still use
        // the raw liftName value.
        const plottable = hasPlottableCoords(params.row);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            <Tooltip title="Alerts & maintenance for this lift">
              <span
                onClick={() => liftMaintenancePage(params.row.liftId)}
                style={{ cursor: "pointer", color: "#2027A5", textDecoration: "underline" }}
              >
                {params.row.liftName}
              </span>
            </Tooltip>
            {plottable ? (
              <Tooltip title="Show this lift on the map">
                <PlaceIcon
                  fontSize="small"
                  onClick={() => showLiftOnMap(params.row)}
                  sx={{ color: "#2027A5", cursor: "pointer", flexShrink: 0 }}
                />
              </Tooltip>
            ) : (
              // Shown greyed rather than omitted, so a missing pin reads as
              // "no usable location" instead of looking like a bug. Covers both
              // no coordinates and out-of-range ones (see hasPlottableCoords).
              <Tooltip title="No valid location reported for this lift">
                <PlaceIcon fontSize="small" sx={{ color: "#B0BEC5", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        )
      }, filterOperators
    },
    {
      field: "customerLiftAlias", headerName: "Customer Alias", width: 160,
      renderHeader: () => (
        <span className={"grid-column-header"}>Customer<br />Alias</span>
      ),
      renderCell: (params) => {
        const isEditing = editingAliasId === params.row.liftId;
        if (isEditing) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <TextField
                size="small"
                variant="standard"
                value={aliasInputValue}
                onChange={(e) => setAliasInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAliasSave(params.row.liftId, params.row.liftName); }}
                inputProps={{ maxLength: 64, style: { fontSize: "13px" } }}
                autoFocus
              />
              <Tooltip title="Save">
                <CheckIcon
                  sx={{ fontSize: "16px", cursor: "pointer", color: "#018015" }}
                  onClick={() => handleAliasSave(params.row.liftId, params.row.liftName)}
                />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ fontSize: "13px" }}>{params.row.customerLiftAlias || "—"}</span>
            {canEditAlias && (
              <Tooltip title="Edit alias">
                <EditIcon
                  sx={{ fontSize: "14px", cursor: "pointer", color: "#2027A5", ml: "4px" }}
                  onClick={() => handleAliasEdit(params.row.liftId, params.row.customerLiftAlias)}
                />
              </Tooltip>
            )}
          </Box>
        );
      }, filterOperators
    },
    {
      field: "driverName", headerName: "Driver Name", width: 150, renderCell: (params) => {
        return (
          <>{params.row.driverName ? params.row.driverName : "Unassigned"}</>
        )
      },
    },
    {
      field: "routeName", headerName: "Location", width: 150, renderCell: (params) => {
        if (params.row.inDcGeofence) {
          return params.row.dc
        } else if (params.row.inDcGeofence === false && params.row.routeName !== "On Route") {
          return <span onClick={() => routeDetailsPage(params.row.routeId, params.row.routeName)} style={{ cursor: "pointer", color: "#2027A5", textDecoration: "underline" }}>{params.row.routeName}</span>
        } else {
          return params.row.routeName
        }
      }, filterOperators
    },
    // TE-2066: the "Last Valid Location" column is removed. The colour-dot
    // indicators introduced in TE-2011 made it redundant — Heartbeat Tracking
    // already ages on LAST_VALID_LOCATION_DATE, and its hover detail shows the
    // timestamp this column used to carry. The underlying
    // row.lastValidLocationDate is still read by heartbeatIndicator, so nothing
    // about the indicator or its hover changes; only the column is gone.
    {
      field: "alertCount", headerName: "# of Alerts",   align: "right",width: 80, renderHeader: (params) => (
        <span className={"grid-column-header-no"}>
          # of Alerts
        </span>
      ), renderCell: (params) => {
        return (
          <>
            <span className="rtPaddedDigits-sm">{params.row.alertCount} </span>
          </>
        )
      }, filterOperators
    },
    {
      field: "batteryPercentage", headerName: "Battery Status",  align: 'left', width: 100,
      renderHeader: (params) => (
        <span className={"grid-column-header"}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Battery <br /> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Status
        </span>
      ), renderCell: (params) => {
        return (
          <>
          <span style={{paddingLeft:"25px"}}>
            {(params.row.batteryPercentage || params.row.batteryPercentage === 0) ?
              params.row.batteryPercentage <= 30 ? <img src={batteryIconRed} /> : <img src={batteryIconGreen} />
              : "n/a"}
            {(params.row.batteryPercentage || params.row.batteryPercentage === 0) &&
              params.row.batteryPercentage + "%"
            }
            </span>
          </>
        );
      },  valueFormatter: (value) => {
        const valueFormatted = value;//(value?moment(value).format('hh:mm A'):"");
        return `${value?value:"n/a"}`;
      },filterOperators
    },
    {
      // TE-2012: green / red / grey / n/a indicator replacing the old
      // connected-or-not dot. Antenna state only, no age. Rules live in
      // utils/liftIndicators.js.
      field: "antenna_connected", headerName: "Asset Tracking",   align: "center", width: 100, renderHeader: (params) => (
          <span className={"grid-column-header"}>&nbsp;&nbsp;&nbsp;&nbsp;Asset <br />
            &nbsp;&nbsp;&nbsp;&nbsp;Tracking
          </span>

      ),
      // Publishes the indicator state so quick search, the column filter and
      // CSV export agree with the dot, which a colour alone cannot convey.
      valueGetter: (value, row) => indicatorSortValue(assetTrackingIndicator(row)),
      sortComparator: indicatorSortComparator,
      renderCell: (params) => (
        <IndicatorCell indicator={assetTrackingIndicator(params.row)} />
      ),
      filterOperators
    },
    {
      // TE-2011: new column. Heartbeat age was previously only implied by the
      // Last Valid Location text, which meant a lift silent for months looked
      // the same as one reporting normally.
      field: "heartbeatTracking", headerName: "Heartbeat Tracking", align: "center", width: 100,
      renderHeader: (params) => (
        <span className={"grid-column-header"}>&nbsp;&nbsp;&nbsp;&nbsp;Heartbeat <br />
          &nbsp;&nbsp;&nbsp;&nbsp;Tracking
        </span>
      ),
      valueGetter: (value, row) => indicatorSortValue(heartbeatIndicator(row)),
      sortComparator: indicatorSortComparator,
      renderCell: (params) => (
        <IndicatorCell indicator={heartbeatIndicator(params.row)} />
      ),
      filterOperators
    },
    {
      field: "temperature", headerName: "Temp(F)",   align: "right", width: 70, renderHeader: (params) => (
        <span className={"grid-column-header-no"}>
          Temp <br />
          <span className= {"centeredPaddedRtDigits-sm"} >
            (F)</span>
        </span>

      ), renderCell: (params) => {
        return (
          <>
          <CustomCellNumRender field={(params.row.temperature !== null && params.row.temperature !== undefined) ? params.row.temperature : "n/a"} />
          </>
        )
      }, valueFormatter: (value) => {
        const valueFormatted = value;//(value?moment(value).format('hh:mm A'):"");
        return `${(value !== null && value !== undefined) ? value : "n/a"}`;
      },filterOperators
    },
    {
      field: "totalTravelDistance", headerName: "Total Travel Dist (km)", width: 100, align: "right",
      renderHeader: (params) => (
        <span className={"grid-column-header-no"}>
          Total Travel <br /> Dist (km)
        </span>
      ), renderCell: (params) => {
        const formattedValue = typeof params.row.totalTravelDistance === 'number'   ? NumberWithCommas(params.row.totalTravelDistance.toFixed(1)) : '';
        return (
          <>
            <span className="rtPaddedDigits-sm">{(params.row.totalTravelDistance !== null && params.row.totalTravelDistance !== undefined) ? formattedValue : "n/a"} </span>
          </>
        )
      }, valueFormatter: (value) => {
        const valueFormatted = value;//(value?moment(value).format('hh:mm A'):"");
        return `${(value !== null && value !== undefined) ? value : "n/a"}`;
      },filterOperators
    },
    {
      field: "lastMaintenanceDate", headerName: "Last Maintenance", minWidth: 150, flex: 1, align:"center",renderHeader: (params) => (
        <span className={"grid-column-header"}>
          Last <br /> Maintenance
        </span>

      ), renderCell: (params) => {

        return (
          <>
            <span style={{ whiteSpace: "nowrap" }}>{params.row.lastMaintenanceDate ? moment(params.row.lastMaintenanceDate).format('MMM D, YYYY') : "n/a"}</span>
          </>
        )
      }, valueFormatter: (value) => {
        const valueFormatted = (value ? moment(value).format('MMM D, YYYY') : "n/a");
        return `${valueFormatted}`;
      }, filterOperators
    },
    {
      field: "maintenanceDueDate", headerName: "Maintenance Due Date", minWidth: 150, flex: 1, align:"center", renderHeader: (params) => (
        <span className={"grid-column-header"} style={{textAlign:"center",paddingLeft:"10px"}}>
          Maintenance <br />Due Date
        </span>

      ),
      renderCell: (params) => {
        if (!params?.row) return <></>;
        const maintenanceDueDate = params.row.maintenanceDueDate;
        if (!maintenanceDueDate) return <span style={{ whiteSpace: "nowrap" }}>n/a</span>;
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        const dateObj = new Date(maintenanceDueDate);
        const formattedDate = dateObj.toLocaleDateString('en-US', options);
        const start = new Date().setHours(0, 0, 0, 0);
        const end = new Date(maintenanceDueDate).setHours(0, 0, 0, 0);
        const differenceInDays = (end - start) / (1000 * 3600 * 24);
        if (differenceInDays > 0 && differenceInDays < 8)
          return <span style={{ whiteSpace: "nowrap" }}>
            {differenceInDays === 1 ? differenceInDays + " Day" : differenceInDays + " Days"}
          </span>;
        return <span style={{ whiteSpace: "nowrap" }}>{formattedDate}</span>;
      }, valueFormatter: (value) => {
        if (!value) return "n/a";
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(value).toLocaleDateString('en-US', options);
      }, filterOperators
    },
    // {
    //   field: "totalPowerOnHours", headerName: "Total Power on hrs",  align: 'center', width: 100, renderHeader: (params) => (
    //     <span className={"grid-column-header"}>
    //       Total Power <br />on hrs
    //     </span>
    //   ), renderCell: (params) => {
    //     return (
    //       <>
    //       <span className="rtPaddedDigits-sm">{params.row.totalPowerOnHours ? NumberWithCommas(params.row.totalPowerOnHours) : "NA"}</span>

    //       </>
    //     )
    //   }, filterOperators
    // },
    {
      field: "totalWorkingHours", headerName: "Total Working hrs", align: 'center',  renderHeader: (params) => (
        <span className={"grid-column-header"}>
          Total <br /> Working hrs
        </span>
      ), renderCell: (params) => {
        return (
          <>
            <span className="rtPaddedDigits-sm">{(params.row.totalWorkingHours !== null && params.row.totalWorkingHours !== undefined) ? NumberWithCommas(params.row.totalWorkingHours) : "NA"}</span>
          </>
        )
      }, filterOperators
    },
    {
      // Cells show only the version number (text before the first "-"). The
      // valueGetter makes quick search, the column filter and CSV export use
      // the same trimmed value.
      field: "firmwareVersion", headerName: "Firmware Version", minWidth: 210, flex: 1,
      valueGetter: (value) => formatFirmwareVersion(value),
      // Header carries the latest released version and, for RpcAdmin, an
      // inline editor for it. See FirmwareVersionHeader.
      renderHeader: (params) => (
        <FirmwareVersionHeader
          latestVersion={latestFirmwareVersion}
          error={latestFirmwareError}
          userRoleName={userRoleName}
          onSave={(value) => updateLatestFirmware({ latestFirmwareVersion: value })}
        />
      ), renderCell: (params) => {
        return (
          <span style={{ whiteSpace: "nowrap" }}>{formatFirmwareVersion(params.row.firmwareVersion)}</span>
        )
      }, filterOperators
    }
  ];

  let onGridResize = () => {
    console.log("Grid Resize");
  }

  const handleToggle = (liftId, liftStatus) => {
    setLiftId(liftId)
    setLiftStatus(liftStatus)
    setOpen(true)
  }

  const handleAliasEdit = (liftId, currentAlias) => {
    setEditingAliasId(liftId);
    setAliasInputValue(currentAlias || "");
  };

  const handleAliasSave = (liftId, liftName) => {
    setPendingAlias({ liftId, liftName, value: aliasInputValue });
    setAliasConfirmOpen(true);
  };

  const handleAliasConfirm = () => {
    updateAlias({ liftId: pendingAlias.liftId, customerLiftAlias: pendingAlias.value });
    setAliasConfirmOpen(false);
    setEditingAliasId(null);
  };

  const handleAliasConfirmClose = () => {
    setAliasConfirmOpen(false);
  };

  const handleClose = () => {
    setOpen(false)
  };

  const handleUpdateStatus = (liftId, liftStatus) => {
    setIsClicked(true);
    // The API still expects "In Service" / "Out of Service"; only the label changed.
    const liftData = {
      liftId: liftId,
      status: nextLiftApiStatus(liftStatus)
    }
    updateLiftStatus(liftData);
  }

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const sortedColumn = sortModel[0];
      let sortedRows;
      if (sortedColumn.field === "routeName" || sortedColumn.field === "driverName") {
        sortedRows = [...liftList].sort((a, b) =>
          String(sortedColumn.sort === "asc" ? a[sortedColumn.field] : b[sortedColumn.field]).localeCompare(
            String(sortedColumn.sort === "asc" ? b[sortedColumn.field] : a[sortedColumn.field])
          )
        );
        sortedRows.sort((a, b) =>
          a[sortedColumn.field] === b[sortedColumn.field] ?
            String(sortedColumn.sort === "asc" ? a.liftName.localeCompare(b.liftName) : b.liftName.localeCompare(a.liftName)) :
            0
        );
        sortedRows.sort((a, b) => {
          const valueA = a[sortedColumn.field];
          const valueB = b[sortedColumn.field];
          if (valueA === null && valueB === null) {
            return 0;
          } else if (valueA === null) {
            return 1;
          } else if (valueB === null) {
            return -1;
          }
        })
      } else {
        sortedRows = [...liftList].sort((a, b) => {
          const numA = parseFloat(a[sortedColumn.field]);
          const numB = parseFloat(b[sortedColumn.field]);
          const dateA = new Date(a[sortedColumn.field]);
          const dateB = new Date(b[sortedColumn.field]);
          if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
            return sortedColumn.sort === "asc" ? dateA - dateB : dateB - dateA;
          }
          if (!isNaN(numA) && !isNaN(numB)) {
            return sortedColumn.sort === "asc" ? numA - numB : numB - numA;
          }
          return String(sortedColumn.sort === "asc" ? a[sortedColumn.field] : b[sortedColumn.field]).localeCompare(
            String(sortedColumn.sort === "asc" ? b[sortedColumn.field] : a[sortedColumn.field])
          );
        });
      }
      setSortLiftList(sortedRows);
    } else {
      setSortLiftList(liftList);
    }
  };

  useEffect(() => {
    if(updateStatus){
      const customerLocationdata = {
        customerId: selectedCustomerListData.customerId,
        locationId: selectedLocationListData.locationId
      }
      if (selectedCustomerId && selectedLocationId) {
        getLiftSummary(customerLocationdata);
        getLiftList(customerLocationdata);
      }
      setOpen(false);
      setIsClicked(false);
    }
  }, [updateStatus]);

  useEffect(() => {
    if (updateAliasStatus) {
      const customerLocationdata = {
        customerId: selectedCustomerListData.customerId,
        locationId: selectedLocationListData.locationId
      };
      if (selectedCustomerId && selectedLocationId) {
        getLiftList(customerLocationdata);
      }
    }
  }, [updateAliasStatus]);

  useEffect(()=>{
    setSortLiftList("")
  },[liftList])

  return (
    <CustomPaper height="100%" padding="10px">
      {open &&
        <React.Fragment>
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "40px" }}>
                <img src={toggleIcon} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <Box style={{ paddingLeft: "40px", paddingRight: "40px" }}>
                  {/* TE-2066: name the target state instead of saying "change
                      the status", which left the user to work out which way the
                      toggle was going. The lift id keeps its own highlighted
                      line, so the sentence is split around it. */}
                  <Typography color="#58595B">
                    Are you sure you want to set Lift
                  </Typography>
                  <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", color: "#2027A5" }}> {liftId} </Box>
                  <Typography color="#58595B" style={{ textAlign: "center" }}>
                    {isSpareStatus(liftStatus) ? "as In-Service?" : "as a Spare unit?"}
                  </Typography>
                  <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "30px", paddingBottom: "40px" }}>
                    <Button
                      variant="contained"
                      style={{ textTransform: 'none', maxWidth: "80px", minWidth: "80px" }}
                      onClick={() => {
                        if (!isClicked) {
                          handleUpdateStatus(liftId, liftStatus);
                        }
                      }}
                      disabled={isClicked}>
                      Yes
                    </Button>
                    <Button variant="outlined" style={{ textTransform: 'none', borderColor: "#58595B", color: "#58595B", marginLeft: "20px", maxWidth: "80px", minWidth: "80px" }} onClick={handleClose}>No</Button>
                  </Box>
                  <Box style={{display:"flex", justifyContent:"center", alignItems:"center"}}>
                    {isClicked ? <CircularProgress size={24} color="inherit" /> : ""}
                  </Box>
                </Box>
              </DialogContentText>
            </DialogContent>
          </Dialog>
        </React.Fragment>
      }
      {aliasConfirmOpen &&
        <React.Fragment>
          <Dialog
            open={aliasConfirmOpen}
            onClose={handleAliasConfirmClose}
            aria-labelledby="alias-dialog-title"
            aria-describedby="alias-dialog-description"
          >
            <DialogTitle id="alias-dialog-title">
              <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "40px" }}>
                <EditIcon style={{ fontSize: "40px", color: "#2027A5" }} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alias-dialog-description">
                <Box style={{ paddingLeft: "40px", paddingRight: "40px" }}>
                  <Typography color="#58595B" style={{ textAlign: "center" }}>
                    Set customer alias for lift
                  </Typography>
                  <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", color: "#2027A5", fontWeight: "bold" }}>
                    {pendingAlias.liftName}
                  </Box>
                  <Typography color="#58595B" style={{ textAlign: "center", marginTop: "8px" }}>
                    New alias: <strong>{pendingAlias.value || "(clear)"}</strong>
                  </Typography>
                  <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "30px", paddingBottom: "40px" }}>
                    <Button
                      variant="contained"
                      style={{ textTransform: 'none', maxWidth: "80px", minWidth: "80px" }}
                      onClick={handleAliasConfirm}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="outlined"
                      style={{ textTransform: 'none', borderColor: "#58595B", color: "#58595B", marginLeft: "20px", maxWidth: "80px", minWidth: "80px" }}
                      onClick={handleAliasConfirmClose}
                    >
                      No
                    </Button>
                  </Box>
                </Box>
              </DialogContentText>
            </DialogContent>
          </Dialog>
        </React.Fragment>
      }
      <AdditionalInfoDialog
        open={additionalInfoOpen}
        onClose={() => setAdditionalInfoOpen(false)}
        rows={LIFT_FIELD_DEFINITIONS}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h3" sx={{ padding: "0rem 1rem 1rem 0.5rem" }}>
          Lift Details
        </Typography>
        {/* Rendered as a real <button> so it is keyboard reachable; the Route
            Status equivalent is a plain div with onClick. Styling matches. */}
        <Box
          component="button"
          type="button"
          onClick={() => setAdditionalInfoOpen(true)}
          data-testid="lift-additional-info-trigger"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            background: "none",
            border: "none",
            padding: 0,
            paddingRight: "15px",
            cursor: "pointer",
            color: "#0C4480",
            fontSize: isMobile() ? "12px" : "14px",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          <InfoIcon color="primary" />
          Additional Information
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%'
        }}
      >
        <div style={{ height: 500, width: "100%" }}>
          <DataGridPro
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            loading={liftDataLoad}
            autoWidth
            rows={liftDataLoad ? [] : sortLiftList ? sortLiftList : liftList}
            columns={columns}
            getRowId={(row) => `${row.liftId}-${row.lastReportedTime}`}
            // getRowId={(row) => row.liftId}
            sx={{
              borderColor: "#FFF !important",
              display: "grid",
              gridTemplateRows: "auto 1f auto",
              "&, [class^=MuiDataGrid]": { border: "none" },
              '& .MuiDataGrid-columnHeader': {
                fontSize: "12px"
              },
              '& .MuiDataGrid-cell': {
                fontSize: "13px",
                border: 1,
                borderRight: 0,
                borderTop: 0,
                borderLeft: 0,
                borderColor: "#D4DFE8",
                display: "flex",
                alignItems: "center",
              },
              '& .MuiDataGrid-cell:focus': {
                outline: ' none'
              },
              '& .hot': {
                "textWrap": "wrap !important",
                "textAlign": "left"
              },
            }}
            slots={{
              toolbar: GridToolbar,
              noResultsOverlay: () => (
                <Box
                  sx={{
                    display: "flex", height: "100%", justifyContent: "center",
                    alignItems: "center", flexDirection: "column",
                  }}>
                  <Typography style={{ fontWeight: "400", fontSize: "16px" }}>
                    No Results Found
                  </Typography>
                </Box>
              ),
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex", height: "100%", justifyContent: "center",
                    alignItems: "center", flexDirection: "column",
                  }}>
                  <Typography style={{ fontWeight: "400", fontSize: "16px" }}>
                    No Rows
                  </Typography>
                </Box>
              ),
            }}
            getCellClassName={(params) => {
              if (params.field === 'lastReportedTime' || params.field === 'maintenanceDueDate') {
                return 'hot';
              }
            }}
            pagination={true}
            slotProps={{ toolbar: { showQuickFilter: true, }, }}
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
            pageSizeOptions={[50,100, 500, 1000,2000,5000,10000]}
            initialState={{
              pagination: { paginationModel: { pageSize: 1000 } },
              // Maintenance columns are hidden by default (no customer uses
              // them yet). Users can still turn them on from COLUMNS.
              columns: { columnVisibilityModel: DEFAULT_HIDDEN_LIFT_COLUMNS },
            }}
            hideScrollbar={true}
            disableRowSelectionOnClick
          />
        </div>
      </Box>
    </CustomPaper>
  );
}

const mapStateToProps = (state) => {
  return {
    updateStatus: state?.Routes?.updateStatus,
    updateAliasStatus: state?.Routes?.updateAliasStatus,
    selectedCustomerListData: state?.Routes?.selectedCustomerListData,
    selectedLocationListData: state?.Routes?.selectedLocationListData,
    userRoleName: state?.Routes?.userRoleName,
    latestFirmwareVersion: state?.Routes?.latestFirmwareVersion ?? null,
    latestFirmwareError: state?.Routes?.latestFirmwareError ?? null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getLiftList: (customerLocationdata) => dispatch(getLiftsList(customerLocationdata)),
    getLiftSummary: (customerLocationdata) => dispatch(getLiftsSummary(customerLocationdata)),
    updateLiftStatus: (liftData) => dispatch(updateLiftStatus(liftData)),
    updateAlias: (liftData) => dispatch(updateLiftAlias(liftData)),
    getLatestFirmware: () => dispatch(getLatestFirmwareVersion()),
    updateLatestFirmware: (data) => dispatch(updateLatestFirmwareVersion(data)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(LiftDataTable);
